// src/components/AcarsPhotoScanner.js
//
// Lets the pilot snap/upload a photo of the FMC "ACARS-FLT LOG" screen.
// The photo is sent to the server as base64 JSON, where it's parsed by
// Claude's vision API (see server/routes/acarsPhotoRoutes.js). The
// extracted OUT/OFF/ON/IN times (always UTC/Zulu on that screen) come back
// and are shown for confirmation before being applied to the form.
//
// No client-side OCR library, no CSP concerns — this is just a normal
// same-origin API call, same as everything else in the app.

import { useState, useRef } from 'react';
import { Camera, X, Check, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
});

// Reads a File as a base64 string (no "data:image/...;base64," prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Props:
 *  - onApply(times): called with { out, off, on, in } (each a 4-digit HHMM
 *    string or null) when the pilot confirms the extracted values.
 */
const AcarsPhotoScanner = ({ onApply }) => {
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState(null); // { out, off, on, in } | null
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setScanning(true);
    setError('');
    setPreview(null);

    try {
      const base64 = await fileToBase64(file);

      const response = await axios.post(
        '/api/acars/parse-photo',
        { image: base64, mediaType: file.type || 'image/jpeg' },
        getAuthHeaders()
      );

      const times = response.data;

      if (!times.out && !times.off && !times.on && !times.in) {
        setError("Couldn't find any OUT/OFF/ON/IN times in that photo. Try a clearer, more zoomed-in shot of the ACARS log screen.");
      } else {
        setPreview(times);
      }
    } catch (err) {
      console.error('ACARS photo scan failed:', err);
      const serverMessage = err.response?.data?.message;
      setError(serverMessage || 'Scan failed — check your connection and try again.');
    } finally {
      setScanning(false);
    }
  };

  const handleConfirm = () => {
    onApply(preview);
    setPreview(null);
  };

  const handleRetake = () => {
    setPreview(null);
    setError('');
    fileInputRef.current?.click();
  };

  const labelFor = (key) => ({ out: 'Out', off: 'Off', on: 'On', in: 'In' })[key];

  return (
    <div className="mb-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelected}
        className="hidden"
      />

      {!preview && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={scanning}
          className="inline-flex items-center px-4 py-2 border border-sky-300 bg-sky-50 hover:bg-sky-100 disabled:opacity-50 text-sky-700 font-medium rounded-lg transition-colors shadow-sm"
        >
          {scanning ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Scanning photo...
            </>
          ) : (
            <>
              <Camera className="h-4 w-4 mr-2" />
              Scan ACARS Photo (UTC Times)
            </>
          )}
        </button>
      )}

      {error && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
          <AlertTriangle className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">{error}</p>
        </div>
      )}

      {preview && (
        <div className="mt-3 p-4 bg-sky-50 border border-sky-200 rounded-lg">
          <p className="text-sm font-medium text-sky-800 mb-3">
            Detected times (UTC) — check these against the photo before applying:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {['out', 'off', 'on', 'in'].map((key) => (
              <div key={key} className="bg-white rounded-md border border-sky-200 px-3 py-2">
                <div className="text-xs text-sky-600">{labelFor(key)}</div>
                <div className={`text-lg font-semibold ${preview[key] ? 'text-gray-900' : 'text-gray-400'}`}>
                  {preview[key]
                    ? `${preview[key].slice(0, 2)}:${preview[key].slice(2)}`
                    : 'not found'}
                </div>
              </div>
            ))}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleConfirm}
              className="inline-flex items-center px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-lg transition-colors shadow-sm"
            >
              <Check className="h-4 w-4 mr-2" />
              Apply to UTC Fields
            </button>
            <button
              type="button"
              onClick={handleRetake}
              className="inline-flex items-center px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors shadow-sm"
            >
              <X className="h-4 w-4 mr-2" />
              Retake / Choose Different Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcarsPhotoScanner;